import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

export const whatsappOrderUrl = (msg) =>
  "https://wa.me/917303241330?text=" +
  encodeURIComponent(msg || "Hi RIZ N WASH! I'd like to place a laundry pickup order.");

export const WHATSAPP_ORDER_URL = whatsappOrderUrl();

// Smoothly scroll to an element on the current Home page, coordinating with Lenis if active.
function scrollToHash(hash) {
  const id = hash.replace(/^#/, "");
  if (!id) return;
  requestAnimationFrame(() => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 80; // offset for sticky nav
    window.scrollTo({ top: y, behavior: "smooth" });
  });
}

export default function Nav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  // When landing on Home with a hash (e.g. /#services), scroll to that section after mount
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const t = setTimeout(() => scrollToHash(location.hash), 350);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [location.pathname, location.hash]);

  const gotoAnchor = (hash) => (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      scrollToHash(hash);
      // update url without full reload
      window.history.replaceState(null, "", `/${hash}`);
    } else {
      navigate(`/${hash}`);
    }
    setMobileOpen(false);
  };

  const linkCls = ({ isActive }) => (isActive ? "text-rw-ink" : "hover:text-rw-ink");

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl border-b border-rw-ink/10"
      style={{ background: "rgba(249, 249, 246, 0.75)" }}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-16 md:h-24 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 group flex-shrink-0" data-testid="nav-logo">
          <img src="/logos/brand-logo.png" alt="RIZ N WASH" className="h-10 md:h-24 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-rw-ink2">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">Home</NavLink>
          <NavLink to="/services" className={linkCls} data-testid="nav-services-link">Services</NavLink>
          <NavLink to="/pricing" className={linkCls} data-testid="nav-pricing">Pricing</NavLink>
          <NavLink to="/guides" className={linkCls} data-testid="nav-guides">Guides</NavLink>
          <NavLink to="/blog" className={linkCls} data-testid="nav-blog">Journal</NavLink>
          <NavLink to="/track" className={linkCls} data-testid="nav-track">Track order</NavLink>
          <NavLink to="/plans" className={linkCls} data-testid="nav-plans">Weekly plans</NavLink>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={WHATSAPP_ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-dark inline-flex items-center gap-1.5 rounded-full px-3 py-2 md:px-5 md:py-2.5 text-xs md:text-sm font-semibold whitespace-nowrap"
            data-testid="nav-whatsapp-btn"
          >
            Order on WhatsApp
          </a>

          {/* Mobile hamburger */}
          <button
            type="button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden ml-1 w-10 h-10 rounded-full border border-rw-ink/15 flex items-center justify-center text-rw-ink hover:bg-rw-ink hover:text-white transition-colors"
            data-testid="nav-mobile-toggle"
          >
            <span className="relative w-4 h-4">
              <span className={`absolute left-0 top-1 h-[2px] w-4 bg-current transition-transform ${mobileOpen ? "translate-y-[6px] rotate-45" : ""}`} />
              <span className={`absolute left-0 top-[7px] h-[2px] w-4 bg-current transition-opacity ${mobileOpen ? "opacity-0" : "opacity-100"}`} />
              <span className={`absolute left-0 top-[13px] h-[2px] w-4 bg-current transition-transform ${mobileOpen ? "-translate-y-[6px] -rotate-45" : ""}`} />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${
          mobileOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
        data-testid="nav-mobile-drawer"
      >
        <nav className="px-4 pb-5 pt-2 flex flex-col gap-1 text-sm font-medium text-rw-ink border-t border-rw-ink/10 bg-rw-bg/80">
          <NavLink to="/" end onClick={() => setMobileOpen(false)} className="py-3 px-2 rounded-lg hover:bg-rw-bg3" data-testid="nav-mobile-home">
            Home
          </NavLink>
          <NavLink to="/services" onClick={() => setMobileOpen(false)} className="py-3 px-2 rounded-lg hover:bg-rw-bg3" data-testid="nav-mobile-services">
            Services
          </NavLink>
          <NavLink to="/pricing" onClick={() => setMobileOpen(false)} className="py-3 px-2 rounded-lg hover:bg-rw-bg3" data-testid="nav-mobile-pricing">
            Pricing
          </NavLink>
          <NavLink to="/guides" onClick={() => setMobileOpen(false)} className="py-3 px-2 rounded-lg hover:bg-rw-bg3" data-testid="nav-mobile-guides">
            Guides
          </NavLink>
          <NavLink to="/blog" onClick={() => setMobileOpen(false)} className="py-3 px-2 rounded-lg hover:bg-rw-bg3" data-testid="nav-mobile-blog">
            Journal
          </NavLink>
          <NavLink to="/track" onClick={() => setMobileOpen(false)} className="py-3 px-2 rounded-lg hover:bg-rw-bg3" data-testid="nav-mobile-track">
            Track order
          </NavLink>
          <NavLink to="/plans" onClick={() => setMobileOpen(false)} className="py-3 px-2 rounded-lg hover:bg-rw-bg3" data-testid="nav-mobile-plans">
            Weekly plans
          </NavLink>
          <a
            href={WHATSAPP_ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
            className="mt-2 py-3 px-2 rounded-lg font-semibold text-center bg-rw-mint text-rw-ink"
            data-testid="nav-mobile-whatsapp"
          >
            Order on WhatsApp
          </a>
        </nav>
      </div>
    </header>
  );
}
